import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

/**
 * Use this Hook to get the inner text (white space normalized) of part of your component.
 *
 * First use-case: We have a generic component that creates an Aria label based on some props passed in. Most are just
 * strings, so this works, but one prop can be a JSX.Element (so we can pass in a formatted string, or something with an
 * icon). This Hook makes it easy to get the text inside the (rendered) JSX.Element. Just wrap it in something to which
 * you attach the ref, and we're good to go:
 *
 * ```
 * const [titleRef, titleText] = useRefInnerText<HTMLDivElement>('');
 * const { title, bodyText } = this.props;
 * const ariaText = `${titleText} ${bodyText}`;
 * return (
 *   <div aria-label={ariaText}>
 *     <div ref={titleRef}>{title}</div>
 *     <p>{bodyText}</p>
 *   </div>
 * );
 * ```
 *
 * @param initialValue {string} the initial (default) value of the element's text, before rendering
 * @return {array}
 *
 *   - First element is the ref to be applied to an element in the component.
 *   - Second element is the inner text of that element, or the initial value (if the component hasn't been rendered
 *     yet).
 */
const useRefInnerText = <T extends HTMLElement = HTMLElement>(initialValue = ''): [RefObject<T>, string] => {
  const ref = useRef<T>(null);
  const [innerText, setInnerText] = useState(initialValue);
  useEffect(() => {
    if (ref.current === null) {
      throw new Error(
        'No current value on the ref, even after rendering. Did you remember to add `ref={myRef}` to an element of your component (where `myRef` is the first element of the array returned by this Hook)?'
      );
    }
    setInnerText(ref.current.innerText?.replace(/\s+/g, ' ').trim());
  }, []);
  return [ref, innerText];
};

export default useRefInnerText;
